import {observer} from 'mobx-react';
import React from 'react';
import {X5Lesson} from '../../../domain';
import {
  DisplayError,
  Link,
  PageHeading,
  PromiseMessage,
  PromiseRetry
} from '../../../lib/components';
import {ReactNodeGenerator} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {AddLessonControls} from './parts/AddLessonControls';
import {AddLessonForm} from './parts/AddLessonForm';
import {DeleteLessonControls} from './parts/DeleteLessonControls';
import {EditLessonControls} from './parts/EditLessonControls';
import {EditLessonForm} from './parts/EditLessonForm';
import {MoveLessonControls} from './parts/MoveLessonControls';
import {MoveLessonForm} from './parts/MoveLessonForm';
import {ReorderLessonsControls} from './parts/ReorderLessonsControls';
import {X4UnitBreadcrumbs} from './X4UnitBreadcrumbs';
import {X4UnitStore} from './X4UnitStore';
import {userStatusService} from '../../../domain'

function LessonViewNoActions(lesson: X5Lesson): React.ReactNode {
  return (
    <div className="row" key={'lesson-' + lesson.Id}>
      <div className="col">
        <div className="text-box">
          {lesson.LessonType}
        </div>
      </div>
      <div className="col">
        <div className="text-box">
          {lesson.Name}
        </div>
      </div>
    </div>
  );
}

function* ViewModeItems(store: X4UnitStore): ReactNodeGenerator {
  const lessons = store.loader.lessons;

  if (lessons.length === 0)
    yield (
      <div className="row justify-content-center" key="no-lessons">
        <div className="col-auto">
          <div className="alert alert-info">
            No lessons
          </div>
        </div>
      </div>);

  for (const lesson of lessons) {
    yield (
      <div className="row" key={'lesson-' + lesson.Id}>
        <div className="col">
          <div className="text-box">
            {lesson.LessonType}
          </div>
        </div>
        <div className="col">
          <div className="text-box">
            {lesson.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <Link className="btn btn-primary" to={routes.x5lesson(lesson.Id)}> View </Link>
          {
            userStatusService.status.roles.ContentManager &&
            <>
              <button type="button" className="btn btn-primary"
                      onClick={() => store.beginEdit(lesson.Id)}> Edit
              </button>
              <button type="button" className="btn btn-primary"
                      onClick={() => store.beginMove(lesson.Id)}> Move
              </button>
              <button type="button" className="btn btn-danger"
                      onClick={() => store.beginDelete(lesson.Id)}> Delete
              </button>
            </>
          }
        </div>
      </div>);
  }
}

function* AddModeItems(store: X4UnitStore): ReactNodeGenerator {
  const lessons = store.loader.lessons;

  for (const lesson of lessons)
    yield LessonViewNoActions(lesson);

  yield (<AddLessonForm key="add-lesson-form"
                        store={store}/>);

  yield (<AddLessonControls key="add-lesson-controls"
                            store={store}/>);
}

function* EditModeItems(store: X4UnitStore): ReactNodeGenerator {
  const lessons = store.loader.lessons;
  const editLessonId = store.editLesson!.lesson.Id;

  for (const lesson of lessons) {
    if (lesson.Id !== editLessonId) {
      yield LessonViewNoActions(lesson);
      continue;
    }

    yield (<EditLessonForm key="edit-lesson-form"
                           store={store}/>);
    yield (<EditLessonControls key="edit-lesson-controls"
                               store={store}/>);
  }
}

function* MoveModeItems(store: X4UnitStore): ReactNodeGenerator {
  const lessons = store.loader.lessons;
  const moveLessonId = store.move!.lesson.Id;

  for (const lesson of lessons) {
    yield LessonViewNoActions(lesson);

    if (lesson.Id !== moveLessonId)
      continue;

    yield (<MoveLessonForm key="move-lesson-form"
                           store={store}/>);

    yield (<MoveLessonControls key="move-lesson-controls"
                               store={store}/>);
  }
}

function* DeleteModeItems(store: X4UnitStore): ReactNodeGenerator {
  const lessons = store.loader.lessons;
  const deleteLessonId = store.deleteLesson!.lessonId;

  for (let lesson of lessons) {
    yield LessonViewNoActions(lesson);

    if (lesson.Id !== deleteLessonId) {
      continue;
    }

    yield (<DeleteLessonControls key="delete-lesson-controls"
                                 store={store}/>);
  }
}

function* ReorderModeItems(store: X4UnitStore): ReactNodeGenerator {
  const lessonIds = store.reorder!.lessonIds;
  const lessonById = store.loader.lessonById;

  for (let index = 0; index < lessonIds.length; index++) {
    let lessonId = lessonIds[index];
    const lesson = lessonById[lessonId];
    const first = index === 0;
    const last = index === lessonIds.length - 1;

    yield (
      <div className="row" key={'lesson-' + lesson.Id}>
        <div className="col">
          <div className="text-box">
            {lesson.LessonType}
          </div>
        </div>
        <div className="col">
          <div className="text-box">
            {lesson.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderUp(index)}
                  disabled={first}>
            <span className="fas fa-arrow-up"/>
          </button>
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderDown(index)}
                  disabled={last}>
            <span className="fas fa-arrow-down"/>
          </button>
        </div>
      </div>);
  }

  yield (<ReorderLessonsControls key="reorder-lessons-controls"
                                 store={store}/>);
}

function* cardBodyRenderer(store: X4UnitStore): ReactNodeGenerator {
  const mode = store.mode;

  switch (mode) {
    case 'View':
      yield* ViewModeItems(store);
      break;
    case 'Edit':
      yield* EditModeItems(store);
      break;
    case 'Add':
      yield* AddModeItems(store);
      break;
    case 'Delete':
      yield* DeleteModeItems(store);
      break;
    case 'ReOrder':
      yield* ReorderModeItems(store);
      break;
    case 'Move':
      yield* MoveModeItems(store);
      break;
  }
}

function* sceneRenderer(store: X4UnitStore): ReactNodeGenerator {
  const loaderState = store.loader.state;
  const isPending = loaderState?.isPending === true;
  const isFailure = loaderState?.isRejected === true;
  const isSuccess = loaderState?.isResolved === true;
  const unit = store.loader.unit;

  if (isPending || isFailure) {
    yield (<PageHeading key="heading"> Unit </PageHeading>);

    yield (<PromiseMessage key="lookup-loader"
                           label="Loading unit"
                           state={loaderState}
                           align="center"/>);

    yield (<PromiseRetry key="lookup-retry"
                         state={loaderState}
                         retry={store.loader.load}
                         align="center"/>);
    return;
  }

  if (!isSuccess)
    return;

  if (unit === null) {
    yield (
      <DisplayError key="unit-not-found">
        Unit with Id {store.unitId} not found
      </DisplayError>);
    return;
  }

  yield (<X4UnitBreadcrumbs key="unit-breadcrumbs" unit={unit}/>);

  yield (<PageHeading key="heading"> Unit {unit!.Name} </PageHeading>);

  yield (
    <div className="row" key="main">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col"> Lessons</div>
              {
                store.mode === 'View' &&
                <div className="col-auto no-wrap">
                  {
                    userStatusService.status.roles.ContentManager &&
                    <>
                      <button type="button" className="btn btn-primary"
                              onClick={store.beginAdd}> Add
                      </button>
                      <button type="button" className="btn btn-primary"
                              disabled={store.loader.lessons.length < 2}
                              onClick={store.beginReorder}> Reorder
                      </button>
                    </>
                  }
                </div>
              }
            </div>
          </div>

          <div className="card-body">
            {Array.from(cardBodyRenderer(store))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  id: number;
}

@observer
export class X4UnitComponent extends React.Component<Props> {
  readonly store: X4UnitStore;

  constructor(props: Readonly<Props>) {
    super(props);
    this.store = new X4UnitStore(props.id);
  }

  componentDidMount() {
    this.store.loader.load();
  }

  render() {
    return Array.from(sceneRenderer(this.store));
  }

  componentWillUnmount(): void {
    this.store.dispose();
  }
}
